/**
 * Quote model — the FULL multi-line quote (MOCKUP_4 · 2026-06-16).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  The owner ruled (2026-06-16) for the FULL quote module — a detailed
 *  multi-line quote for big/custom jobs, alongside the simple Set-price path.
 *  This is the PURE engine for it: line items (a service from the rate card +
 *  qty + rate → line amount), add/remove lines, a whole-quote discount, GST
 *  driven by the CURRENCY (₹ → GST 18% as CGST 9% + SGST 9%; $ → export of
 *  service, zero-rated, no GST — MOCKUP_4 legend §6), firm routing (the SAME
 *  locked rule: USD/GST → Aayatan Veda · INR-no-GST → RKH HUF), and a grand
 *  total.
 *
 *  THE ONE SPINE (no parallel money path): when a quote is ACCEPTED its
 *  total + currency flow into the EXISTING agreed-price plumbing —
 *  `acceptedAgreedPrice()` produces exactly the `{ tier, currency, value,
 *  gst }` shape `setProjectAgreedPrice` / `applyLeadPrice` already consume, so
 *  the accepted total seeds `gateAmounts` + `feeHistory` through the one
 *  canonical resolver. The quote NEVER writes gates itself.
 *
 *  Reuses `rateCard.ts` (`calculateL3B`, `ServiceCatalogEntry`,
 *  `REFRESHED_CATALOGUE`) for line rates + `firms.ts` for routing. PURE +
 *  dependency-light (no Firebase, no React) so the Node test runner exercises
 *  every branch directly. Persistence is `quoteStore.ts`.
 * ─────────────────────────────────────────────────────────────────────────
 */
import type { Currency, FirmId } from './firms';
import { firmFor, getFirm } from './firms';
import { composeQuoteServiceLine, isUseType, type UseType } from './engagementPhrase';
import {
  REFRESHED_CATALOGUE,
  calculateL3B,
  type ServiceCatalogEntry,
  type ServiceMilestone,
} from './rateCard';

/* ════════════════════ Quote status ════════════════════ */

// The live states: the forward path (draft → sent → accepted → paid) PLUS the
// retired states a sent quote can move to (superseded/expired via `retireQuote`,
// rejected on a lost deal). The reader MUST understand all of them, else a
// retired quote falls through to 'draft' and the list lies about its state.
export type QuoteStatus =
  | 'draft'
  | 'sent'
  | 'accepted'
  | 'paid'
  | 'superseded'
  | 'expired'
  | 'rejected';

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  paid: 'Paid',
  superseded: 'Superseded',
  expired: 'Expired',
  rejected: 'Rejected',
};

/** The full set of persisted quote statuses (drives the reader's coercion). */
export const QUOTE_STATUSES: QuoteStatus[] = [
  'draft',
  'sent',
  'accepted',
  'paid',
  'superseded',
  'expired',
  'rejected',
];

/* ════════════════════ Line items ════════════════════ */

/** One line on a quote — a service (rate-card entry) at a qty + rate. */
export interface QuoteLine {
  /** Stable line id (for React keys / edits). */
  id: string;
  /** The rate-card svcId this line came from ('' for a free-text custom line). */
  svcId: string;
  /** Description shown on the quote (defaults from the catalogue, editable). */
  desc: string;
  /** Optional sub-note (e.g. "per drawing"). */
  note?: string;
  qty: number;
  /** Per-unit rate in the quote's currency. */
  rate: number;
  /**
   * What the client gets for this line — auto-filled from the rate card's
   * `ServiceCatalogEntry.deliverables` when the service is picked, then
   * editable per deal. '' / undefined when the catalogue carries none.
   */
  deliverables?: string;
}

/**
 * How a payment stage's share is defined:
 *   • 'pct'  — a % of the grand total (amt derived = pct × grandTotal).
 *   • 'amt'  — a locked ₹ / $ value the staffer typed (pct back-computed).
 *   • 'auto' — the balancer: absorbs the remainder so the stages sum EXACTLY
 *              to the grand total. Exactly ONE stage is 'auto' (default: last).
 */
export type MilestoneMode = 'pct' | 'amt' | 'auto';

/**
 * A payment-stage milestone carried on the quote (auto-filled from the rate
 * card's `ServiceCatalogEntry.defaultMilestones` for the headline service,
 * editable). Mirrors `ServiceMilestone` so the eventual project inherits them.
 *
 * BACKWARD COMPAT: docs written before the amount work carry only
 * `{label,isAdvance,pct,dueRule}` — a missing `mode` is treated as 'pct' and a
 * missing `amt` resolves to `pct × grandTotal` (see `resolveMilestoneAmounts`).
 */
export interface QuoteMilestone {
  label: string;
  isAdvance: boolean;
  /** How this stage's share was defined (drives the ₹ ↔ % resolution). */
  mode: MilestoneMode;
  /** % of the grand total this stage collects (kept ₹ ↔ % consistent). */
  pct: number;
  /** The resolved ₹ / $ amount this stage collects (= pct × grandTotal, or a typed value). */
  amt: number;
  dueRule: string;
  deliverables?: string;
}

/** PURE — coerce any raw milestone-ish value to a well-formed QuoteMilestone. */
function normalizeMilestone(raw: Partial<QuoteMilestone> | null | undefined): QuoteMilestone {
  const m = (raw ?? {}) as Partial<QuoteMilestone>;
  const mode: MilestoneMode =
    m.mode === 'amt' || m.mode === 'auto' || m.mode === 'pct' ? m.mode : 'pct';
  const amt = Number(m.amt) || 0;
  return {
    label: String(m.label ?? 'Stage'),
    isAdvance: m.isAdvance === true,
    mode,
    pct: Number(m.pct) || 0,
    amt: amt > 0 ? Math.round(amt) : 0,
    dueRule: String(m.dueRule ?? 'on_milestone'),
    ...(m.deliverables != null ? { deliverables: String(m.deliverables) } : {}),
  };
}

/** The result of resolving stage amounts against a grand total. */
export interface ResolvedMilestones {
  /** Milestones with `amt` filled + `pct` recomputed; exactly one is 'auto'. */
  milestones: QuoteMilestone[];
  /** true when the locked (non-auto) amounts exceed the grand total (auto clamped 0). */
  overAllocated: boolean;
}

/**
 * PURE — resolve each stage's ₹ amount against the grand total so the stages
 * sum EXACTLY to it:
 *   • 'amt' stages keep their locked amount.
 *   • 'pct' stages = round(pct × grandTotal / 100).
 *   • exactly ONE stage is the auto-balancer that absorbs the remainder
 *     (the first stage explicitly marked 'auto', else the LAST stage). It is
 *     returned with `mode: 'auto'` so the ₹/% never drift by a rounding rupee.
 * Every stage's `pct` is recomputed from its resolved `amt` so ₹ and % stay
 * consistent. No negative amounts: if the locked amounts exceed the grand total
 * the auto stage clamps to 0 and `overAllocated` is set true.
 */
export function resolveMilestoneAmounts(
  input: QuoteMilestone[] | null | undefined,
  grandTotal: number,
): ResolvedMilestones {
  const gt = Math.max(0, Math.round(Number(grandTotal) || 0));
  const ms = (input ?? []).map(normalizeMilestone);
  if (ms.length === 0) return { milestones: [], overAllocated: false };

  // The balancer: the first explicit 'auto', else the last stage by default.
  let autoIdx = ms.findIndex((m) => m.mode === 'auto');
  if (autoIdx < 0) autoIdx = ms.length - 1;

  // Resolve every non-auto stage's amount from its mode; sum them.
  let sumOthers = 0;
  const resolved: QuoteMilestone[] = ms.map((m, i) => {
    if (i === autoIdx) return { ...m, mode: 'auto', amt: 0 };
    const amt =
      m.mode === 'amt'
        ? Math.max(0, Math.round(Number(m.amt) || 0))
        : Math.max(0, Math.round(((Number(m.pct) || 0) * gt) / 100));
    sumOthers += amt;
    // A stray second 'auto' (not the chosen balancer) degrades to 'pct'.
    return { ...m, mode: m.mode === 'auto' ? 'pct' : m.mode, amt };
  });

  let overAllocated = false;
  let autoAmt = gt - sumOthers;
  if (autoAmt < 0) {
    autoAmt = 0;
    overAllocated = true;
  }
  resolved[autoIdx] = { ...resolved[autoIdx], mode: 'auto', amt: autoAmt };

  // Recompute each stage's pct from its resolved amt (₹ ↔ % consistent).
  for (const m of resolved) {
    m.pct = gt > 0 ? Math.round((m.amt / gt) * 10000) / 100 : 0;
  }
  return { milestones: resolved, overAllocated };
}

/**
 * PURE — the amount due NOW: the first stage's resolved amount when there are
 * stages, else the grand total. A single stage (or a single 100% stage) resolves
 * to the full grand total naturally (the lone stage is the balancer).
 */
export function quotePayNow(
  milestones: QuoteMilestone[] | null | undefined,
  grandTotal: number,
): number {
  const gt = Math.max(0, Math.round(Number(grandTotal) || 0));
  if (!milestones || milestones.length === 0) return gt;
  return resolveMilestoneAmounts(milestones, gt).milestones[0].amt;
}

/**
 * PURE — the bracket reason for the "You pay now" line, derived from the stages:
 *   • no stages / a single stage (the full payment) → "in full".
 *   • 2+ stages, first is the advance → "for advance".
 *   • 2+ stages, otherwise → "for {short label}" (the first stage's label
 *     trimmed to its head before any —/–/·/:). Never blank.
 */
export function payNowReason(milestones: QuoteMilestone[] | null | undefined): string {
  if (!milestones || milestones.length <= 1) return 'in full';
  const first = milestones[0];
  if (first.isAdvance) return 'for advance';
  const head = String(first.label || '').split(/[—–·:]/)[0].trim();
  return head ? `for ${head}` : 'for advance';
}

/** PURE — a line's amount = qty × rate (rounded, never negative). */
export function lineAmount(line: QuoteLine): number {
  const amt = (Number(line.qty) || 0) * (Number(line.rate) || 0);
  return amt > 0 ? Math.round(amt) : 0;
}

/* ════════════════════ Discount ════════════════════ */

export type DiscountKind = 'pct' | 'amount';

export interface QuoteDiscount {
  kind: DiscountKind;
  /** % (0–100) when kind='pct', else a flat amount in the quote currency. */
  value: number;
}

/** PURE — the discount amount in currency units for a given subtotal. */
export function discountAmount(
  subtotal: number,
  discount: QuoteDiscount | null | undefined,
): number {
  if (!discount || !(discount.value > 0)) return 0;
  if (discount.kind === 'pct') {
    const pct = Math.min(100, Math.max(0, discount.value));
    return Math.round((subtotal * pct) / 100);
  }
  return Math.min(subtotal, Math.round(discount.value));
}

/* ════════════════════ Tax (currency-driven — MOCKUP_4 §6) ════════════════════ */

/** The GST rate AV bills on ₹ work (firm1). $ work is export / zero-rated. */
export const GST_RATE = 18;

export interface QuoteTax {
  /** true → GST applies (₹ + GST-needed). false → zero-rated / exempt. */
  applies: boolean;
  /** The taxable base (subtotal − discount). */
  base: number;
  ratePct: number;
  cgst: number;
  sgst: number;
  total: number;
  /** Human label for the no-tax case ('' when tax applies). */
  exemptReason: string;
}

/**
 * PURE — the tax block for a quote. The CURRENCY decides it, never the staffer
 * (MOCKUP_4 §6): a ₹ quote that needs a GST invoice carries GST 18% split into
 * CGST 9% + SGST 9% (intra-state — both firms are Chhattisgarh); a $ quote is
 * "Export of service — zero-rated, no GST"; a ₹ quote WITHOUT a GST invoice
 * (the HUF / RKH path) carries no tax either. `gst` is the "GST invoice
 * needed?" answer (only meaningful for ₹; USD is always export).
 */
export function quoteTax(
  base: number,
  currency: Currency,
  gst: boolean,
): QuoteTax {
  const taxable = base > 0 ? base : 0;
  if (currency === 'USD') {
    return {
      applies: false,
      base: taxable,
      ratePct: 0,
      cgst: 0,
      sgst: 0,
      total: 0,
      exemptReason: 'Export of service — zero-rated, no GST (LUT).',
    };
  }
  if (!gst) {
    return {
      applies: false,
      base: taxable,
      ratePct: 0,
      cgst: 0,
      sgst: 0,
      total: 0,
      exemptReason: 'No GST invoice — billed by Rohit Khandelwal (HUF), GST-exempt.',
    };
  }
  const half = Math.round((taxable * (GST_RATE / 2)) / 100);
  return {
    applies: true,
    base: taxable,
    ratePct: GST_RATE,
    cgst: half,
    sgst: half,
    total: half * 2,
    exemptReason: '',
  };
}

/* ════════════════════ Structured discount breakdown (optional) ════════════════════
 *
 *  The whole-quote `discount` (pct/amount) is the LEGACY opaque number. On top
 *  of it, a quote MAY carry two OPTIONAL, structured, itemised reductions so the
 *  discount is explainable to the client instead of a single number:
 *    • `referralCredit`         — flat "Referral / relationship credit" in the
 *                                 quote currency.
 *    • `priorServiceAdjustment` — flat "Adjustment for prior service" already paid.
 *  Both are ADDITIVE flat reductions summed WITH the legacy discount into one
 *  `totalDiscount` that reduces the taxable base — no double-counting: the legacy
 *  `discount` is itself; each new field is itself; they simply stack. When BOTH
 *  are absent (every legacy quote), `totalDiscount === discount` and the whole
 *  roll-up is byte-identical to the pre-breakdown behaviour.
 */

export interface QuoteAdjustments {
  referralCredit?: number | null;
  priorServiceAdjustment?: number | null;
}

/** PURE — a stored money-ish input → a non-negative, rounded currency amount. */
export function resolveAdjustment(v: number | null | undefined): number {
  const n = Number(v) || 0;
  return n > 0 ? Math.round(n) : 0;
}

/** PURE — pull the optional structured breakdown off a quote (null-safe). */
export function quoteAdjustments(
  q: Pick<Quote, 'referralCredit' | 'priorServiceAdjustment'>,
): QuoteAdjustments {
  return {
    referralCredit: q.referralCredit ?? null,
    priorServiceAdjustment: q.priorServiceAdjustment ?? null,
  };
}

/* ════════════════════ Totals ════════════════════ */

export interface QuoteTotals {
  subtotal: number;
  /** The LEGACY whole-quote discount amount (from the pct/amount `discount`). */
  discount: number;
  /** Structured "Referral / relationship credit" (0 when absent). */
  referralCredit: number;
  /** Structured "Adjustment for prior service" (0 when absent). */
  priorServiceAdjustment: number;
  /** discount + referralCredit + priorServiceAdjustment (feeds the base). */
  totalDiscount: number;
  taxableBase: number;
  tax: QuoteTax;
  /** Grand total = (subtotal − totalDiscount) + tax. */
  grandTotal: number;
}

/**
 * PURE — the full totals roll-up for a set of lines. `adjustments` is OPTIONAL:
 * omit it (or pass null) and the result is identical to the legacy behaviour
 * (`totalDiscount === discount`, same taxable base, same grand total). Pass the
 * structured breakdown and its two flat reductions stack on the legacy discount.
 */
export function quoteTotals(
  lines: QuoteLine[],
  currency: Currency,
  gst: boolean,
  discount: QuoteDiscount | null | undefined,
  adjustments?: QuoteAdjustments | null,
): QuoteTotals {
  const subtotal = lines.reduce((s, l) => s + lineAmount(l), 0);
  const disc = discountAmount(subtotal, discount);
  const referralCredit = resolveAdjustment(adjustments?.referralCredit);
  const priorServiceAdjustment = resolveAdjustment(adjustments?.priorServiceAdjustment);
  const totalDiscount = disc + referralCredit + priorServiceAdjustment;
  const taxableBase = Math.max(0, subtotal - totalDiscount);
  const tax = quoteTax(taxableBase, currency, gst);
  return {
    subtotal,
    discount: disc,
    referralCredit,
    priorServiceAdjustment,
    totalDiscount,
    taxableBase,
    tax,
    grandTotal: taxableBase + tax.total,
  };
}

/* ════════════════════ The Quote ════════════════════ */

export interface Quote {
  /** Stable quote id (QT-#### placeholder; Firestore mints the real id). */
  id: string;
  status: QuoteStatus;

  /* — who it's for — */
  /** Lead id this quote was raised from (the front-door origin), if any. */
  leadId: string | null;
  /** Project id once the quote's owning engagement exists / is converted. */
  projectId: string | null;
  clientId: string | null;
  clientName: string;
  /* — delivery party (2026-07-03 owner fix: "no channel delivered") —
   * The recipient's name/phone/email SNAPSHOT, stamped at compose-from-lead
   * so the send always has a destination. These are PRECEDENCE-1 for both
   * the portal resolver (`resolveQuoteRecipientContact`) and the deployed
   * send CF (`resolveQuoteRecipient` step 1 "quote_stamped") — previously
   * only the CF stamped them AFTER a successful send, so a lead whose
   * top-level email mirror was stale delivered on NO channel. Not in the
   * create rule's forbidden list; the CF refreshes them post-send. */
  leadName?: string | null;
  leadPhone?: string | null;
  leadEmail?: string | null;

  /* — what — */
  /** The headline tier/service the quote is for (drives the agreed-price seed). */
  tier: string;
  /** Display name of the tier (locked Set-4 name). */
  serviceName: string;
  lines: QuoteLine[];
  discount: QuoteDiscount | null;
  /* — structured discount breakdown (optional; all legacy quotes have none) —
   * Itemise the opaque `discount` so the quote can explain the price. Both
   * amounts are flat, in the quote currency, and stack ON TOP of `discount`
   * (see QuoteAdjustments / quoteTotals). Absent ⇒ display + math identical to
   * a plain quote. Each amount carries its OWN optional short note, printed
   * beside its line on the internal quote view. */
  /** "Referral / relationship credit" — a flat reduction (e.g. 350). */
  referralCredit?: number;
  /** Short note sitting WITH the referral credit (why / who referred). */
  referralNote?: string;
  /** "Adjustment for prior service" already paid — a flat reduction (e.g. 40). */
  priorServiceAdjustment?: number;
  /** Short note sitting WITH the prior-service adjustment (which service). */
  priorServiceNote?: string;
  /* — composed service line inputs (optional) —
   * `useType` seeds/overrides the property noun in the composed service line
   * (auto-fetched from the lead's propertyType, overridable on the form).
   * `serviceDescription`, when set, is a MANUAL override that wins over the
   * composed description. Both absent ⇒ the line composes from tier + useType,
   * or falls back to the bare serviceName. */
  useType?: UseType;
  serviceDescription?: string;
  /**
   * DENORMALISED, resolved service line — the FINAL composed string
   * (`composeQuoteServiceLine`) persisted on the doc so the backend send reads
   * it directly and never re-imports the engagementPhrase catalog. Recomputed
   * on every save (tier / useType / serviceDescription changes flow in). Always
   * a string (falls back to the bare serviceName), never undefined on the doc.
   */
  serviceLine?: string;
  /**
   * Payment-stage milestones — auto-filled from the headline service's
   * `defaultMilestones` in the rate card, editable per deal. Carried onto the
   * quote doc so the sent quote + the eventual project reflect them. Empty []
   * when the catalogue service has no milestones (degrade gracefully).
   */
  milestones: QuoteMilestone[];

  /* — money — */
  currency: Currency;
  /** "GST invoice needed?" — only meaningful for ₹ (USD is always export). */
  gst: boolean;
  /** Billing firm — auto-routed from {currency, gst}, never stored stale. */
  firmId: FirmId;

  /* — lifecycle — */
  /** ISO date the quote is valid until (default +15 days). */
  validUntil: string | null;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  acceptedAt: string | null;
  /** Channels the quote was sent on (audit). */
  sentChannels: string[];
  /**
   * READ-ONLY — the secure pay link the send CF mints (CF-owned; the portal
   * never writes it, it is a forbidden create/update field). Present only after
   * a quote has been sent; the send-preview shows it when present, else a muted
   * "added when the quote is sent" note.
   */
  paymentLinkUrl?: string | null;
  /**
   * READ-ONLY — the AV-hosted pay page (portal.aayatanveda.in/pay/<token>),
   * CF-owned like paymentLinkUrl. This is the link actually sent to clients
   * (paymentLinkUrl is the raw Razorpay/PayPal fallback) — prefer this one
   * wherever a quote's pay link is displayed or sent.
   */
  payPageUrl?: string | null;

  /* — Setu credit (L1C → L2x, 2026-06-21 spec §2.3) — */
  /**
   * System-computed credits applied to this quote (NOT manual discounts — a
   * manual ₹11,000 off ₹60,000 = 18.3% would trip the >10% admin-gate; modelling
   * it as a catalogue-driven credit line keeps it in Sales authority + auditable).
   * Written by the `computeSetuCredit` CF on quote create; the client never
   * authors it. Each entry reduces the agreed grand total by `amount`.
   */
  creditsApplied?: SetuCredit[];
  /**
   * Audit lineage — the source quoteNo/id this quote was raised FROM (an L2x
   * quote raised off a paid L1C, OR — since 2026-07-07 — the quote a SUPERSEDE-
   * and-revise copied). NOT a `revisionOf` — a fresh quote that references its
   * origin (spec §2.4). Drives the "Revised from #<old>" chip on the new quote.
   */
  relatedFrom?: string | null;
  /**
   * SUPERSEDE-and-revise back-ref (2026-07-07) — set on the OLD quote when it is
   * superseded by an editable copy: the id of the NEW draft that replaces it.
   * Drives the "Replaced by #<new>" chip on the old quote. Stamped in the SAME
   * sent→superseded write; NOT a CF-immutable field, so the update rule allows
   * it alongside the status flip.
   */
  supersededBy?: string | null;
  /**
   * REVISION / version number of this quote in its supersede-and-revise chain
   * (2026-07-07). An ORIGINAL quote is revision 1 (absent/undefined ⇒ treat as 1
   * for display + math). Each supersede-and-revise copy bumps it:
   * `revision = (source.revision ?? 1) + 1` — so original=1, first revise=2, … .
   * A PLAIN field (not a CF-owned/forbidden create field) so it passes the
   * create rule and round-trips through `quoteCreateBody` / `quoteFromDoc`. The
   * PDF reads `quote.revision` to print the same "Rev N".
   */
  revision?: number;
}

/** PURE — a quote's display revision number (absent/undefined/≤0 ⇒ 1). */
export function quoteRevision(quote: Pick<Quote, 'revision'>): number {
  const n = Math.round(Number(quote.revision) || 0);
  return n > 0 ? n : 1;
}

/** One system-computed credit line on a quote (the Setu L1C→L2x credit). */
export interface SetuCredit {
  /** Stable id ('setu'). */
  id: string;
  /** Human label shown on the quote/PDF, e.g. "Setu credit (L1C → L2C)". */
  label: string;
  /** Credit amount in the quote currency (subtracted from the grand total). */
  amount: number;
  /** The source L1C quoteNo this credit derives from (audit). */
  sourceQuoteNo: string;
  /** The source L1C quote doc id (for the consume-on-accept stamp). */
  sourceQuoteId: string;
  /** ISO timestamp the credit was computed (audit). */
  appliedAt: string;
}

/** PURE — total credit applied on a quote (0 when none). Never negative. */
export function quoteCreditTotal(quote: Pick<Quote, 'creditsApplied'>): number {
  const list = quote.creditsApplied || [];
  const sum = list.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  return sum > 0 ? Math.round(sum) : 0;
}

/** PURE — a blank line with a fresh id. */
export function blankLine(id: string): QuoteLine {
  return { id, svcId: '', desc: '', qty: 1, rate: 0, deliverables: '' };
}

/**
 * PURE — build a quote line from a rate-card service in a given currency.
 * L3B is formula-priced — pass `l3bFloors` to compute it, else the rate is 0
 * (the staffer types it). qty defaults 1.
 */
export function lineFromService(
  id: string,
  svc: ServiceCatalogEntry,
  currency: Currency,
  opts?: { qty?: number; l3bFloors?: { label: string; sqft: number; countsAs?: number }[] },
): QuoteLine {
  let rate: number;
  if (svc.isFormula && opts?.l3bFloors && opts.l3bFloors.length > 0) {
    rate = calculateL3B({
      floors: opts.l3bFloors,
      currency: currency === 'USD' ? 'USD' : 'INR',
    }).amount;
  } else {
    rate = currency === 'USD' ? svc.rUSD : svc.rINR;
  }
  return {
    id,
    svcId: svc.svcId,
    desc: svc.name,
    note: svc.desc ? undefined : undefined,
    qty: opts?.qty && opts.qty > 0 ? Math.round(opts.qty) : 1,
    rate: rate > 0 ? Math.round(rate) : 0,
    // AUTO-FILL the rate card's deliverables onto the line (editable later).
    // Degrade gracefully: a service with no `deliverables` → '' (not error).
    deliverables: svc.deliverables ?? '',
  };
}

/**
 * PURE — the default payment-stage milestones for a rate-card service, mapped
 * to the quote's `QuoteMilestone` shape. Empty [] when the service carries no
 * `defaultMilestones` (degrade gracefully — never throws).
 */
export function milestonesFromService(svc: ServiceCatalogEntry | null | undefined): QuoteMilestone[] {
  if (!svc || !Array.isArray(svc.defaultMilestones)) return [];
  return svc.defaultMilestones.map((m: ServiceMilestone) => ({
    label: m.label,
    isAdvance: m.isAdvance === true,
    // Rate-card defaults are percent-based templates; the ₹ amount resolves per
    // quote from the grand total (see resolveMilestoneAmounts).
    mode: 'pct' as MilestoneMode,
    pct: Number(m.pct) || 0,
    amt: 0,
    dueRule: m.dueRule,
    deliverables: m.deliverables != null ? m.deliverables : undefined,
  }));
}

/**
 * PURE — resolve the headline service of a quote from the catalogue (by svcId
 * tier, else by tier field). Drives the milestone auto-fill at create + when
 * the tier changes. null when nothing matches.
 */
export function headlineService(
  tier: string,
  catalogue: ServiceCatalogEntry[] = REFRESHED_CATALOGUE,
): ServiceCatalogEntry | null {
  return (
    catalogue.find((s) => s.svcId === tier) ??
    catalogue.find((s) => s.tier === tier) ??
    null
  );
}

/**
 * PURE — a fresh draft quote. Seeds ONE line from the lead's tier (so the
 * detailed builder opens pre-filled with the standard service, matching the
 * simple path's standard price), in the given currency. `validUntil` = +15d.
 */
export function newQuote(input: {
  id: string;
  leadId?: string | null;
  projectId?: string | null;
  clientId?: string | null;
  clientName: string;
  /** Delivery party snapshot (compose-from-lead) — see Quote.leadPhone/leadEmail. */
  leadName?: string | null;
  leadPhone?: string | null;
  leadEmail?: string | null;
  tier: string;
  serviceName?: string;
  currency?: Currency;
  gst?: boolean;
  now: string;
  catalogue?: ServiceCatalogEntry[];
}): Quote {
  const currency: Currency = input.currency === 'USD' ? 'USD' : 'INR';
  const gst = typeof input.gst === 'boolean' ? input.gst : currency !== 'USD';
  const cat = input.catalogue ?? REFRESHED_CATALOGUE;
  const svc =
    cat.find((s) => s.svcId === input.tier) ??
    cat.find((s) => s.tier === input.tier) ??
    null;
  const lines: QuoteLine[] = svc
    ? [lineFromService('ln1', svc, currency)]
    : [blankLine('ln1')];
  return {
    id: input.id,
    status: 'draft',
    leadId: input.leadId ?? null,
    projectId: input.projectId ?? null,
    clientId: input.clientId ?? null,
    clientName: input.clientName,
    leadName: input.leadName ?? null,
    leadPhone: input.leadPhone ?? null,
    leadEmail: input.leadEmail ?? null,
    tier: input.tier,
    serviceName: input.serviceName ?? svc?.name ?? input.tier,
    lines,
    discount: null,
    // AUTO-FILL the headline service's default milestones (editable per deal).
    milestones: milestonesFromService(svc),
    currency,
    gst,
    firmId: firmFor({ currency, gst }),
    validUntil: addDaysISO(input.now, 15),
    createdAt: input.now,
    updatedAt: input.now,
    sentAt: null,
    acceptedAt: null,
    sentChannels: [],
  };
}

/** PURE — ISO date n days from an ISO date (date-only output). */
export function addDaysISO(iso: string, days: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * PURE — re-derive the firm whenever currency/gst change (never store stale).
 * Returns a NEW quote with `firmId` reconciled + `updatedAt` bumped.
 */
export function reconcileQuote(quote: Quote, now: string): Quote {
  const gst = quote.currency === 'USD' ? false : quote.gst;
  return {
    ...quote,
    gst,
    firmId: firmFor({ currency: quote.currency, gst }),
    updatedAt: now,
  };
}

/* ════════════════════ The agreed-price BRIDGE (the one spine) ════════════════════
 *
 *  THE INVARIANT (no parallel money path): a quote's accepted GRAND TOTAL is
 *  what the client pays — exactly the figure the agreed-price spine seeds into
 *  the money gate. `acceptedAgreedPrice` returns the SAME input shape
 *  `setProjectAgreedPrice` (live project) and `applyLeadPrice` (lead) consume,
 *  so the accepted total flows through the ONE canonical `resolveGateAmount`
 *  → `seedGateAmounts` → `feeHistory`. The quote module owns NO gate math.
 */

export interface AcceptedAgreedPrice {
  tier: string;
  currency: Currency;
  /** The grand total — the agreed figure the client pays. */
  value: number;
  /** GST flag carried to the agreed-price write (firm routing). */
  gst: boolean;
}

/**
 * PURE — the agreed-price input an accepted quote produces. The `value` is the
 * GRAND TOTAL (incl. GST when it applies — that is the real amount the client
 * pays and the gate must expect). This is the ONLY bridge from a quote to
 * money; callers pass it straight to `setProjectAgreedPrice` / `applyLeadPrice`.
 */
export function acceptedAgreedPrice(quote: Quote): AcceptedAgreedPrice {
  // Pass the structured breakdown so the agreed price the money spine seeds is
  // the SAME grand total the quote displays — never a figure that ignores the
  // itemised reductions.
  const totals = quoteTotals(
    quote.lines,
    quote.currency,
    quote.gst,
    quote.discount,
    quoteAdjustments(quote),
  );
  // Setu credit (§6.1) — the credit is PART of the resolved agreed price, not a
  // side-channel: the NET (grand total − credit) is what the client pays and the
  // figure the ONE resolver seeds into the gate. Never below 0.
  const net = Math.max(0, totals.grandTotal - quoteCreditTotal(quote));
  return {
    tier: quote.tier,
    currency: quote.currency,
    value: net,
    gst: quote.currency === 'USD' ? false : quote.gst,
  };
}

/* ════════════════════ Contact spine — id resolver (AD-039) ════════════════════
 *
 *  A quote's contact link IS its `clientId` (no new field — spec §7). When the
 *  quote was raised before the client master existed, `clientId` may be null;
 *  the contact is then resolvable from the project (`project.clientId`, the
 *  source-of-truth once converted) or the front-door lead (`lead.contactId`,
 *  the spine link). This PURE id-only resolver mirrors the precedence of the
 *  impure `resolveQuoteRecipientContact` (clientId → project→client → lead), so
 *  the store can stamp `clientId` on save/accept and the UI can show the single
 *  contact anchor. No money path here — purely the identity link.
 */

/**
 * PURE — resolve the `clients/{cid}` id a quote points to, by precedence:
 *   1. the quote's own `clientId` (already stamped — the strongest)
 *   2. `ctx.projectClientId` — the project's `clientId` (source-of-truth once
 *      the engagement exists)
 *   3. `ctx.leadContactId` — the front-door lead's `contactId` (the spine link)
 * Returns null when none resolves (the quote has no contact yet).
 */
export function resolveQuoteContactId(
  quote: Pick<Quote, 'clientId' | 'projectId' | 'leadId'>,
  ctx: { leadContactId?: string | null; projectClientId?: string | null },
): string | null {
  return (
    quote.clientId ??
    ctx.projectClientId ?? // project.clientId (source-of-truth on the project)
    ctx.leadContactId ?? // lead.contactId (front-door spine)
    null
  );
}

/* ════════════════════ Display helpers ════════════════════ */

/** PURE — the firm short name for a quote (billing identity chip). */
export function quoteFirmShort(quote: Quote): string {
  return getFirm(quote.firmId).shortName;
}

/**
 * PURE — money in the quote's currency (Indian grouping for ₹). USD renders as
 * "US$" (NOT a bare "$") to match the backend `formatMoney`
 * (avbp_quote_send.js) so the portal display, the send preview, and the sent
 * WhatsApp/email read identically.
 */
export function quoteMoney(n: number, currency: Currency): string {
  return currency === 'USD'
    ? `US$${Math.round(n).toLocaleString('en-US')}`
    : `₹${Math.round(n).toLocaleString('en-IN')}`;
}

/* ════════════════════ Firestore doc seam (PURE shapes) ════════════════════
 *
 *  ⚠ THE DEPLOYED QUOTE CONTRACT (verified 2026-06-16 against prod rules +
 *  prod triggers — supersedes the earlier "team-gated, no rules change" note):
 *  `quotes/` is governed by a STRICT deployed `firestore.rules` block AND a
 *  live trigger suite (`onQuoteCreate_stampDefaults`, `onQuoteWrite_recalc-
 *  Totals`, `onQuoteUpdate_guardStateMachine`, `…promoteLeadToClient`). The
 *  client may ONLY write a bare, rule-valid draft; the server stamps the rest.
 *  The CREATE rule (a) requires `status=='draft'`, (b) requires a non-empty
 *  `leadId` OR a non-empty `projectId` (project-origin is accepted by the live
 *  2026-07-03 ruleset), and
 *  (c) FORBIDS `quoteNo` / `validUntil` / `total` / payment fields on create
 *  (the `stampDefaults` trigger fills them). `recalcTotals` recomputes `total`
 *  from a LEGACY `items[]` array (`{svcTier,qty,rate}`) + numeric `discount`
 *  (a percentage) + numeric `tax` (a percentage) — so we MIRROR our `lines[]`
 *  UI shape onto `items[]` + write `discount`/`tax` as numbers, or the server
 *  zeroes `total`. The UPDATE rule is a tri-mode state gate (draft→sent,
 *  sent→accepted in payment/manual mode, …) with CF-only-field immutability;
 *  status transitions are ALSO enforced (and reverted) by `guardStateMachine`.
 *
 *  These PURE mappers keep the conformant doc shapes unit-tested. The impure
 *  read/write lives in quoteStore.ts.
 */

/**
 * PURE — map our `lines[]` to the LEGACY `items[]` shape `recalcTotals` reads
 * (`{desc, svcTier, qty, rate, amount}`). svcId → svcTier (the tier code the
 * recalc keys L3B off); custom lines carry the quote's headline tier so the
 * recalc still treats them as a flat qty×rate line.
 */
/** PURE — the persisted shape of one quote line (carries deliverables). */
export function lineDocShape(l: QuoteLine): Record<string, unknown> {
  return {
    id: l.id,
    svcId: l.svcId,
    desc: l.desc,
    ...(l.note ? { note: l.note } : {}),
    qty: l.qty,
    rate: l.rate,
    ...(l.deliverables ? { deliverables: l.deliverables } : {}),
  };
}

/** PURE — the persisted shape of one quote milestone. */
export function milestoneDocShape(m: QuoteMilestone): Record<string, unknown> {
  return {
    label: m.label,
    isAdvance: m.isAdvance === true,
    // `mode` records how the stage was defined; `amt` is the resolved ₹ value —
    // this is what fixes the backend `milestones[0].amt === 0` advance bug.
    mode: m.mode === 'amt' || m.mode === 'auto' ? m.mode : 'pct',
    pct: Number(m.pct) || 0,
    amt: Math.max(0, Math.round(Number(m.amt) || 0)),
    dueRule: m.dueRule,
    ...(m.deliverables ? { deliverables: m.deliverables } : {}),
  };
}

export function quoteItems(quote: Quote): Record<string, unknown>[] {
  return quote.lines.map((l) => ({
    desc: l.desc,
    svcTier: l.svcId || quote.tier,
    qty: l.qty,
    rate: l.rate,
    amount: lineAmount(l),
  }));
}

/**
 * PURE — the legacy NUMERIC discount percentage `recalcTotals` expects (it
 * applies `subtotal × discount%`). A %-discount passes straight through; a
 * flat-amount discount is converted to the equivalent % of subtotal so the
 * server's `total` matches our `grandTotal`. 0 when none.
 */
export function discountPct(
  lines: QuoteLine[],
  discount: QuoteDiscount | null | undefined,
): number {
  if (!discount || !(discount.value > 0)) return 0;
  if (discount.kind === 'pct') return Math.min(100, Math.max(0, discount.value));
  const subtotal = lines.reduce((s, l) => s + lineAmount(l), 0);
  if (subtotal <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((discount.value / subtotal) * 10000) / 100));
}

/**
 * PURE — the legacy NUMERIC tax percentage `recalcTotals` expects. GST applies
 * (18) only for a ₹ + gst quote; USD (export) and ₹-no-GST (HUF) are 0. This
 * mirrors `quoteTax` so the server `total` lands on the same grand total.
 */
export function taxPct(currency: Currency, gst: boolean): number {
  return currency !== 'USD' && gst ? GST_RATE : 0;
}

/**
 * PURE — the CREATE body for a NEW quote: a BARE, rule-VALID draft. It writes
 * ONLY fields the deployed CREATE rule permits — `status:'draft'`, a non-empty
 * `leadId`, `acceptanceMode` (drives the accept path + state machine) — and
 * NONE of the forbidden fields (`quoteNo` / `validUntil` / `total` / payment
 * fields are stamped by `stampDefaults`/`recalcTotals`). Carries both our
 * `lines[]` (UI source of truth) and the legacy `items[]`/`discount`(%)/`tax`(%)
 * mirror so `recalcTotals` computes `total` correctly. The live rule accepts a
 * non-empty `leadId` OR a non-empty `projectId`, so project-origin quotes save.
 */
export function quoteCreateBody(quote: Quote): Record<string, unknown> {
  return {
    status: 'draft',
    acceptanceMode: 'manual', // manual accept = the team marks it accepted (no payment-link gate)
    leadId: quote.leadId ?? '',
    projectId: quote.projectId,
    clientId: quote.clientId,
    clientName: quote.clientName,
    // Delivery party snapshot (2026-07-03 "no channel delivered" fix) — the
    // recipient the send CF + portal resolver read at PRECEDENCE 1. Only
    // written when present (never null-stomps); NOT in the create rule's
    // forbidden list; the CF refreshes them after each send.
    ...(quote.leadName ? { leadName: quote.leadName } : {}),
    ...(quote.leadPhone ? { leadPhone: quote.leadPhone } : {}),
    ...(quote.leadEmail ? { leadEmail: quote.leadEmail } : {}),
    tier: quote.tier,
    serviceName: quote.serviceName,
    // our UI source of truth (carries the auto-filled per-line deliverables)
    lines: quote.lines.map(lineDocShape),
    // the auto-filled (editable) payment-stage milestones — carried onto the
    // quote doc so the sent quote + the eventual project reflect them.
    milestones: quote.milestones.map(milestoneDocShape),
    // legacy mirror so `recalcTotals` derives `total` (and doesn't zero it)
    items: quoteItems(quote),
    discount: discountPct(quote.lines, quote.discount),
    tax: taxPct(quote.currency, quote.gst),
    // our discount object (round-trips the kind/value the UI edits)
    discountObj: quote.discount ? { kind: quote.discount.kind, value: quote.discount.value } : null,
    // structured discount breakdown (optional) — always written (0 / null when
    // empty) so revising a quote can CLEAR them and they round-trip cleanly.
    // NOT in the create rule's forbidden list; no server trigger touches them.
    referralCredit: resolveAdjustment(quote.referralCredit),
    referralNote: quote.referralNote?.trim() || null,
    priorServiceAdjustment: resolveAdjustment(quote.priorServiceAdjustment),
    priorServiceNote: quote.priorServiceNote?.trim() || null,
    // composed-service-line inputs (optional) — the property use type + a manual
    // service-description override. Always written so edits/clears round-trip.
    useType: quote.useType ?? null,
    serviceDescription: quote.serviceDescription?.trim() || null,
    // FINAL resolved service line for the backend send (recomputed every save;
    // always a string — bare serviceName when the tier has no engagement phrase).
    serviceLine: composeQuoteServiceLine(quote),
    currency: quote.currency,
    gst: quote.gst,
    firmId: quote.firmId,
    // denormalised grand total for the list view (server `total` is authority).
    // NOTE: `subtotal` (backend {{3}}) is NOT written here — it is a CF-owned
    // field the `onQuoteWrite_recalcTotals` trigger computes from `items[]`
    // (which we DO write) and stamps on the doc; it equals this builder's
    // subtotal (Σ line amounts). Writing it from the client is forbidden by the
    // deployed update rule (would break the draft save).
    grandTotal: quoteTotals(
      quote.lines,
      quote.currency,
      quote.gst,
      quote.discount,
      quoteAdjustments(quote),
    ).grandTotal,
    // Setu lineage (§2.4) — when this L2x quote was raised off a paid L1C, carry
    // the source quoteNo. The `computeSetuCredit` CF reads this + the partyId to
    // compute + write `creditsApplied[]` server-side (the client never authors it).
    ...(quote.relatedFrom ? { relatedFrom: quote.relatedFrom } : {}),
    // version number — a plain field (NOT a forbidden CF field), so it passes
    // the create rule. Original=1; a revised draft carries its bumped revision.
    revision: quoteRevision(quote),
    createdAt: quote.createdAt,
  };
}

/**
 * PURE — the SUPERSEDE-and-revise copy (2026-07-07). Produces a FRESH draft
 * `Quote` that copies every EDITABLE field of `source` (the customization the
 * staffer built) so the revised quote opens ready to edit + re-send, and stamps
 * `relatedFrom = source.id` for lineage. It deliberately does NOT carry status /
 * quoteNo / validUntil / sentAt / acceptedAt / payment fields: the copy is a
 * brand-new draft (`status:'draft'`), and the server stamps quoteNo/validUntil
 * on create. Routed through the SAME `createQuote` → `quoteCreateBody` path, so
 * the copy is rule-VALID (draft, a non-empty leadId OR projectId, no forbidden
 * CF fields). `now` seeds createdAt/updatedAt; `validUntil` is left null (the
 * create rule forbids it — the trigger stamps a fresh +15d window).
 */
export function reviseQuoteDraft(source: Quote, id: string, now: string): Quote {
  return {
    id,
    status: 'draft',
    // who it's for — carried verbatim (at least one of leadId/projectId is set
    // on any real quote, satisfying the create rule's origin requirement).
    leadId: source.leadId,
    projectId: source.projectId,
    clientId: source.clientId,
    clientName: source.clientName,
    leadName: source.leadName ?? null,
    leadPhone: source.leadPhone ?? null,
    leadEmail: source.leadEmail ?? null,
    // what — full customization copied (deep copies so edits don't mutate source).
    tier: source.tier,
    serviceName: source.serviceName,
    lines: source.lines.map((l) => ({ ...l })),
    discount: source.discount ? { ...source.discount } : null,
    ...(source.referralCredit ? { referralCredit: source.referralCredit } : {}),
    ...(source.referralNote ? { referralNote: source.referralNote } : {}),
    ...(source.priorServiceAdjustment
      ? { priorServiceAdjustment: source.priorServiceAdjustment }
      : {}),
    ...(source.priorServiceNote ? { priorServiceNote: source.priorServiceNote } : {}),
    ...(source.useType ? { useType: source.useType } : {}),
    ...(source.serviceDescription ? { serviceDescription: source.serviceDescription } : {}),
    milestones: source.milestones.map((m) => ({ ...m })),
    // money — currency/gst/firm carried (firm re-derives from currency/gst anyway).
    currency: source.currency,
    gst: source.gst,
    firmId: source.firmId,
    // lifecycle — a FRESH draft: nothing sent/accepted; server stamps quoteNo +
    // validUntil (forbidden on the client create). No payment fields copied.
    validUntil: null,
    createdAt: now,
    updatedAt: now,
    sentAt: null,
    acceptedAt: null,
    sentChannels: [],
    // lineage — the new draft points back at the quote it revises.
    relatedFrom: source.id,
    // version — the revised draft is the NEXT version of the same underlying
    // quote (original=1, first revise=2, …). Absent source ⇒ treated as 1.
    revision: quoteRevision(source) + 1,
  };
}

/**
 * PURE — the DRAFT-EDIT update body (line/discount/currency edits while the
 * quote is still a draft). Conforms to UPDATE-rule mode (1) (draft→draft) and
 * the CF-only-field immutability: writes NONE of `quoteNo`/`validUntil`/
 * `subtotal`/`total`/`paymentLinkUrl`/`partyId` (the server owns them), and a
 * NUMERIC `discount` (the rule's `discount <= 10` check is numeric; an object
 * would force the admin-only branch). Status stays `draft`.
 */
export function quoteDraftUpdateBody(quote: Quote): Record<string, unknown> {
  return {
    status: 'draft',
    clientName: quote.clientName,
    tier: quote.tier,
    serviceName: quote.serviceName,
    lines: quote.lines.map(lineDocShape),
    milestones: quote.milestones.map(milestoneDocShape),
    items: quoteItems(quote),
    discount: discountPct(quote.lines, quote.discount),
    tax: taxPct(quote.currency, quote.gst),
    discountObj: quote.discount ? { kind: quote.discount.kind, value: quote.discount.value } : null,
    // structured discount breakdown (optional) — always written so a draft edit
    // can CLEAR them (merge-write keeps stamps; explicit 0/null overwrites).
    referralCredit: resolveAdjustment(quote.referralCredit),
    referralNote: quote.referralNote?.trim() || null,
    priorServiceAdjustment: resolveAdjustment(quote.priorServiceAdjustment),
    priorServiceNote: quote.priorServiceNote?.trim() || null,
    // composed-service-line inputs (optional) — always written so edits/clears
    // round-trip on the merge-write.
    useType: quote.useType ?? null,
    serviceDescription: quote.serviceDescription?.trim() || null,
    // FINAL resolved service line for the backend send (recomputed every save;
    // always a string — bare serviceName when the tier has no engagement phrase).
    serviceLine: composeQuoteServiceLine(quote),
    currency: quote.currency,
    gst: quote.gst,
    firmId: quote.firmId,
    // `subtotal` (backend {{3}}) is CF-owned (recalcTotals) — forbidden on the
    // update path, so NOT written here. It is stamped server-side from items[].
    grandTotal: quoteTotals(
      quote.lines,
      quote.currency,
      quote.gst,
      quote.discount,
      quoteAdjustments(quote),
    ).grandTotal,
  };
}

/**
 * PURE — a predicate mirroring the DEPLOYED create rule (firestore.rules
 * ~L478). Lets a unit test prove `quoteCreateBody` is rule-VALID without an
 * emulator. `allowProjectOrigin` = the LIVE rule (2026-07-03 deploy): `leadId`
 * non-empty OR `projectId` non-empty. With it false the predicate models the
 * older leadId-only rule (kept so a test can still assert that stricter shape).
 */
export function passesCreateRule(
  body: Record<string, unknown>,
  opts?: { allowProjectOrigin?: boolean },
): boolean {
  const has = (k: string) => Object.prototype.hasOwnProperty.call(body, k);
  if (body.status !== 'draft') return false;
  const leadOk = typeof body.leadId === 'string' && (body.leadId as string).length > 0;
  const projOk =
    !!opts?.allowProjectOrigin &&
    typeof body.projectId === 'string' &&
    (body.projectId as string).length > 0;
  if (!leadOk && !projOk) return false;
  for (const forbidden of [
    'quoteNo',
    'validUntil',
    'paymentLinkUrl',
    'payPageUrl',
    'paymentConfirmedAt',
    'paymentConfirmedBy',
    'paymentRefNumber',
  ]) {
    if (has(forbidden)) return false;
  }
  return true;
}

/**
 * PURE — a predicate mirroring the proposed DEPLOYED delete rule
 * (`quotes_allow_delete.patch`). Lets a unit test prove the delete-gate logic
 * without an emulator:
 *   • admin OR sales may delete a DRAFT quote
 *   • admin may additionally delete a NON-ACCEPTED quote (sent/rejected/
 *     superseded/expired)
 *   • an ACCEPTED quote is NEVER hard-deletable
 * `role` reflects the caller's claim. Returns whether the delete is allowed.
 */
export function passesDeleteRule(
  status: string,
  role: { admin?: boolean; sales?: boolean },
): boolean {
  const isAdmin = role.admin === true;
  const isSalesOrAdmin = isAdmin || role.sales === true;
  if (status === 'draft') return isSalesOrAdmin;
  if (['sent', 'rejected', 'superseded', 'expired'].includes(status)) return isAdmin;
  return false; // accepted / paid / anything else → never hard-deletable
}

/** PURE — UI gate: can the current role HARD-delete a quote of this status? */
export function canHardDeleteQuote(
  status: QuoteStatus,
  role: { isAdmin: boolean; isSales: boolean },
): boolean {
  return passesDeleteRule(status, { admin: role.isAdmin, sales: role.isSales });
}

/** PURE — the Firestore doc body for a quote (drops the id; stamps not here). */
export function quoteDocBody(quote: Quote): Record<string, unknown> {
  const totals = quoteTotals(
    quote.lines,
    quote.currency,
    quote.gst,
    quote.discount,
    quoteAdjustments(quote),
  );
  return {
    status: quote.status,
    leadId: quote.leadId,
    projectId: quote.projectId,
    clientId: quote.clientId,
    clientName: quote.clientName,
    tier: quote.tier,
    serviceName: quote.serviceName,
    lines: quote.lines.map(lineDocShape),
    milestones: quote.milestones.map(milestoneDocShape),
    discount: quote.discount ? { kind: quote.discount.kind, value: quote.discount.value } : null,
    // structured discount breakdown (optional) — always written (0 / null).
    referralCredit: resolveAdjustment(quote.referralCredit),
    referralNote: quote.referralNote?.trim() || null,
    priorServiceAdjustment: resolveAdjustment(quote.priorServiceAdjustment),
    priorServiceNote: quote.priorServiceNote?.trim() || null,
    // composed-service-line inputs (optional).
    useType: quote.useType ?? null,
    serviceDescription: quote.serviceDescription?.trim() || null,
    // FINAL resolved service line for the backend send (recomputed every save;
    // always a string — bare serviceName when the tier has no engagement phrase).
    serviceLine: composeQuoteServiceLine(quote),
    currency: quote.currency,
    gst: quote.gst,
    firmId: quote.firmId,
    validUntil: quote.validUntil,
    // denormalised total for the list view (the source of truth is the lines).
    // `subtotal` is CF-owned (recalcTotals) — not written from the client.
    grandTotal: totals.grandTotal,
    createdAt: quote.createdAt,
    updatedAt: quote.updatedAt,
    sentAt: quote.sentAt,
    acceptedAt: quote.acceptedAt,
    sentChannels: quote.sentChannels,
    // Setu credit lineage (round-trips the server-written credit + source ref).
    ...(quote.creditsApplied && quote.creditsApplied.length
      ? { creditsApplied: quote.creditsApplied.map((c) => ({ ...c })) }
      : {}),
    ...(quote.relatedFrom ? { relatedFrom: quote.relatedFrom } : {}),
    // version number (original=1) — round-trips the supersede-and-revise chain.
    revision: quoteRevision(quote),
  };
}

/** PURE — map a raw `quotes/{id}` doc to a typed Quote (defensive coercion). */
export function quoteFromDoc(id: string, d: Record<string, unknown>): Quote {
  const currency: Currency = d.currency === 'USD' ? 'USD' : 'INR';
  const gst = currency === 'USD' ? false : d.gst === true;
  // Coerce any KNOWN persisted status (incl. the retired states
  // superseded/expired/rejected) so the list shows the true state; only a
  // genuinely unknown/absent value falls back to 'draft'.
  const status: QuoteStatus = QUOTE_STATUSES.includes(d.status as QuoteStatus)
    ? (d.status as QuoteStatus)
    : 'draft';
  const lines: QuoteLine[] = Array.isArray(d.lines)
    ? (d.lines as unknown[]).map((raw, i) => {
        const l = (raw ?? {}) as Record<string, unknown>;
        return {
          id: String(l.id ?? `ln${i + 1}`),
          svcId: String(l.svcId ?? ''),
          desc: String(l.desc ?? ''),
          note: l.note != null ? String(l.note) : undefined,
          qty: Number(l.qty) || 0,
          rate: Number(l.rate) || 0,
          deliverables: l.deliverables != null ? String(l.deliverables) : '',
        } as QuoteLine;
      })
    : [];
  const milestones: QuoteMilestone[] = Array.isArray(d.milestones)
    ? (d.milestones as unknown[]).map((raw) => {
        const m = (raw ?? {}) as Record<string, unknown>;
        // BACKWARD COMPAT: legacy docs carry only pct — a missing `mode`
        // defaults to 'pct' and a missing `amt` to 0 (resolved from pct on demand).
        const mode: MilestoneMode =
          m.mode === 'amt' || m.mode === 'auto' || m.mode === 'pct'
            ? (m.mode as MilestoneMode)
            : 'pct';
        return {
          label: String(m.label ?? 'Milestone'),
          isAdvance: m.isAdvance === true,
          mode,
          pct: Number(m.pct) || 0,
          amt: Math.max(0, Math.round(Number(m.amt) || 0)),
          dueRule: String(m.dueRule ?? 'on_milestone'),
          deliverables: m.deliverables != null ? String(m.deliverables) : undefined,
        } as QuoteMilestone;
      })
    : [];
  // Discount round-trips via `discountObj` ({kind,value}); the legacy numeric
  // `discount` field (a %) is the server-recalc input, not the UI object. We
  // also tolerate an OLD doc that stored `discount` as an object directly.
  let discount: QuoteDiscount | null = null;
  const dObjRaw =
    d.discountObj && typeof d.discountObj === 'object'
      ? d.discountObj
      : d.discount && typeof d.discount === 'object'
        ? d.discount
        : null;
  if (dObjRaw) {
    const dd = dObjRaw as Record<string, unknown>;
    const kind: DiscountKind = dd.kind === 'amount' ? 'amount' : 'pct';
    const value = Number(dd.value) || 0;
    if (value > 0) discount = { kind, value };
  } else if (typeof d.discount === 'number' && d.discount > 0) {
    // numeric legacy discount → a % discount
    discount = { kind: 'pct', value: Number(d.discount) };
  }
  // Structured discount breakdown (optional) — round-trip. Absent / 0 / blank
  // all normalise to undefined so a plain quote stays byte-identical to today.
  const referralCredit = resolveAdjustment(d.referralCredit as number | null | undefined);
  const priorServiceAdjustment = resolveAdjustment(d.priorServiceAdjustment as number | null | undefined);
  const str = (v: unknown): string | undefined =>
    v != null && String(v).trim() ? String(v).trim() : undefined;
  const referralNote = str(d.referralNote);
  const priorServiceNote = str(d.priorServiceNote);
  const useType: UseType | undefined = isUseType(d.useType) ? d.useType : undefined;
  const serviceDescription = str(d.serviceDescription);
  const serviceLine = str(d.serviceLine);
  const firmId: FirmId =
    d.firmId === 'firm1' || d.firmId === 'firm2'
      ? (d.firmId as FirmId)
      : firmFor({ currency, gst });
  // Setu credit (server-written by computeSetuCredit) — defensive coercion.
  const creditsApplied: SetuCredit[] = Array.isArray(d.creditsApplied)
    ? (d.creditsApplied as unknown[])
        .map((raw) => {
          const c = (raw ?? {}) as Record<string, unknown>;
          const amount = Number(c.amount) || 0;
          return {
            id: String(c.id ?? 'setu'),
            label: String(c.label ?? 'Setu credit'),
            amount: amount > 0 ? Math.round(amount) : 0,
            sourceQuoteNo: String(c.sourceQuoteNo ?? ''),
            sourceQuoteId: String(c.sourceQuoteId ?? ''),
            appliedAt: String(c.appliedAt ?? ''),
          } as SetuCredit;
        })
        .filter((c) => c.amount > 0)
    : [];
  return {
    id,
    status,
    leadId: d.leadId != null ? String(d.leadId) : null,
    projectId: d.projectId != null ? String(d.projectId) : null,
    clientId: d.clientId != null ? String(d.clientId) : null,
    clientName: String(d.clientName ?? ''),
    leadName: d.leadName != null ? String(d.leadName) : null,
    leadPhone: d.leadPhone != null ? String(d.leadPhone) : null,
    leadEmail: d.leadEmail != null ? String(d.leadEmail) : null,
    tier: String(d.tier ?? ''),
    serviceName: String(d.serviceName ?? d.tier ?? ''),
    lines,
    discount,
    ...(referralCredit > 0 ? { referralCredit } : {}),
    ...(referralNote ? { referralNote } : {}),
    ...(priorServiceAdjustment > 0 ? { priorServiceAdjustment } : {}),
    ...(priorServiceNote ? { priorServiceNote } : {}),
    ...(useType ? { useType } : {}),
    ...(serviceDescription ? { serviceDescription } : {}),
    ...(serviceLine ? { serviceLine } : {}),
    milestones,
    currency,
    gst,
    firmId,
    validUntil: d.validUntil != null ? String(d.validUntil) : null,
    createdAt: d.createdAt != null ? String(d.createdAt) : '',
    updatedAt: d.updatedAt != null ? String(d.updatedAt) : '',
    sentAt: d.sentAt != null ? String(d.sentAt) : null,
    acceptedAt: d.acceptedAt != null ? String(d.acceptedAt) : null,
    sentChannels: Array.isArray(d.sentChannels)
      ? (d.sentChannels as unknown[]).map((c) => String(c))
      : [],
    ...(d.paymentLinkUrl != null && String(d.paymentLinkUrl).trim()
      ? { paymentLinkUrl: String(d.paymentLinkUrl).trim() }
      : {}),
    ...(d.payPageUrl != null && String(d.payPageUrl).trim()
      ? { payPageUrl: String(d.payPageUrl).trim() }
      : {}),
    ...(creditsApplied.length ? { creditsApplied } : {}),
    relatedFrom: d.relatedFrom != null ? String(d.relatedFrom) : null,
    supersededBy: d.supersededBy != null ? String(d.supersededBy) : null,
    // version number — absent/≤0 normalises to 1 (a plain original quote).
    revision: quoteRevision({ revision: d.revision as number | undefined }),
  };
}

/* ════════════════════ List-view sort ════════════════════ */

/** PURE — newest-updated first (the Quotes list / record order). */
export function sortQuotes(list: Quote[]): Quote[] {
  return [...list].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
}

/** PURE — a list filter by status ('all' → everything). */
export function filterQuotes(list: Quote[], status: QuoteStatus | 'all'): Quote[] {
  return status === 'all' ? list : list.filter((q) => q.status === status);
}

/** PURE — quotes matching a free-text search over client / service / tier. */
export function searchQuotes(list: Quote[], q: string): Quote[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return list;
  return list.filter((qt) =>
    `${qt.clientName} ${qt.serviceName} ${qt.tier}`.toLowerCase().includes(needle),
  );
}
